import { HttpStatusCode } from 'axios'
import { Result } from '@cplug/frontend-core/utils'
import { BaseGatewayErrorDTO, type DataWrappedDTO } from '@cplug/frontend-core/gateways'
import { isSuccessResponse, ErrorCodes, HttpMethod, type BaseError } from '@cplug/frontend-core/models'
import { type App, SmartMenuFunctionality } from '../models/App'
import type { AppDuplicatePayload } from '../models/AppDuplicatePayload'
import type { AppCredentials } from '../models/AppCredentials'
import type { AppLinkQrToken } from '../models/AppLinkQrToken'
import type { AppRevokeLicensePayload } from '../models/AppRevokeLicensePayload'
import type { VpnSetting } from '../models/AppSetting'
import { mapDTOToApp, mapAppToDTO, AppDTO } from './DTOs/AppDTO'
import { mapAppRevokeLicenseToDTO } from './DTOs/AppRevokeLicenseDTO'
import { AppCredentialsDTO, mapDTOToAppCredentials } from './DTOs/AppCredentialsDTO'
import {
  TenantStoreDomainDTO,
  mapDTOToTenantStoreDomain,
  type TenantStoreDomain
} from './DTOs/TenantStoreDomainDTO'
import { mapDTOToAppLinkQrToken, type AppLinkQrTokenDTO } from './DTOs/AppLinkQrTokenDTO'
import { CPBaseCRUDGateway } from '~/shared/gateways/CPBaseCRUDGateway'

type ListDTO = { apps: AppDTO[] }

export class CPAppGateway extends CPBaseCRUDGateway<AppDTO, ListDTO, App> {
  protected apiUrlDomain = 'apps'

  protected dtoToModelMapper = (dto: AppDTO): Result<App> => Result.ofValue(mapDTOToApp(dto))
  protected modelToDtoMapper = mapAppToDTO
  protected dtoSchema = AppDTO

  protected get listField (): 'apps' {
    return 'apps'
  }

  protected override get modelIdField (): keyof App {
    return 'id'
  }

  async getStoreByCompany (companyId: number): Promise<Result<App>> {
    const response = await this.get<DataWrappedDTO<AppDTO>>(
      `${this.apiUrlDomain}/store/by-company/${companyId}`
    )

    if (response?.status === HttpStatusCode.NotFound) {
      return Result.ofError({ code: ErrorCodes.NotFound })
    }

    return this.parseSingleResponse(response, this.dtoSchema, this.dtoToModelMapper)
  }

  async getTenantStoreDomain (): Promise<Result<TenantStoreDomain>> {
    const response = await this.get<DataWrappedDTO<TenantStoreDomainDTO>>(
      `${this.apiUrlDomain}/store/tenant-domain`
    )

    return this.parseSingleResponse(response, TenantStoreDomainDTO, mapDTOToTenantStoreDomain)
  }

  async restore (id: number): Promise<Result<undefined>> {
    const response = await this.put<DataWrappedDTO<AppDTO>>(`${this.apiUrlDomain}/${id}/restore`)

    if (response?.data == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(undefined)
  }

  async duplicate (app: App, options: AppDuplicatePayload): Promise<Result<App>> {
    const response = await this.post<DataWrappedDTO<AppDTO>>(`${this.apiUrlDomain}/${app.id}/duplicate`, options)

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(mapDTOToApp(response.data.data))
  }

  async getCredentials (appId: number): Promise<Result<AppCredentials>> {
    const response = await this.get<DataWrappedDTO<AppCredentialsDTO>>(`${this.apiUrlDomain}/${appId}/credentials`)

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(mapDTOToAppCredentials(response.data.data))
  }

  async generateLinkQrToken (appId: number): Promise<Result<AppLinkQrToken>> {
    const response = await this.post<DataWrappedDTO<AppLinkQrTokenDTO>>(
      `${this.apiUrlDomain}/${appId}/link-qr-token`,
      {}
    )

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(mapDTOToAppLinkQrToken(response.data.data))
  }

  async generateQuickLinkQrToken (): Promise<Result<AppLinkQrToken>> {
    const response = await this.post<DataWrappedDTO<AppLinkQrTokenDTO>>(
      `${this.apiUrlDomain}/quick-link-qr-token`,
      {}
    )

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(mapDTOToAppLinkQrToken(response.data.data))
  }

  async revokeLicense (appId: number, payload: AppRevokeLicensePayload): Promise<Result<undefined>> {
    const response = await this.post<undefined>(
      `${this.apiUrlDomain}/${appId}/revoke-license`,
      mapAppRevokeLicenseToDTO(payload)
    )

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(undefined)
  }

  async sync (appIds: number[]): Promise<Result<undefined>> {
    if (appIds.length === 0) {
      return Result.ofValue(undefined)
    }
    const response = await this.post<undefined>(`${this.apiUrlDomain}/syncs`, { app_ids: appIds })

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(undefined)
  }

  async getPushNotificationConfig (): Promise<Result<{ firebaseStatusPath: string | null }>> {
    const response = await this.get<DataWrappedDTO<{ firebase_status_path: string | null }>>(
      `${this.apiUrlDomain}/push-notification-config`
    )

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    const data = response.data?.data
    const firebaseStatusPath = data?.firebase_status_path ?? null

    if (firebaseStatusPath != null && firebaseStatusPath.trim().length === 0) {
      return Result.ofError(
        <BaseError>{
          code: ErrorCodes.DataValidation,
          meta: { firebase_status_path: 'Invalid push notification config: path cannot be empty string' }
        }
      )
    }

    return Result.ofValue({ firebaseStatusPath })
  }

  async uploadImage (appId: number, imageBase64: string): Promise<Result<string>> {
    const response = await this.post<DataWrappedDTO<{ image_url: string }>>(
      `${this.apiUrlDomain}/${appId}/image`,
      { image: imageBase64 }
    )

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(response.data.data.image_url)
  }

  async downloadTableQrCode (appId: number, tableId?: number | null): Promise<Result<Blob>> {
    const response = await this.api.request<Blob>({
      url: `${this.apiUrlDomain}/${appId}/download-table-qr-code`,
      method: HttpMethod.POST,
      data: { table_id: tableId ?? null },
      responseType: 'blob'
    })

    if (!isSuccessResponse(response)) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(response.data)
  }

  async downloadQrCode (appId: number, functionality: SmartMenuFunctionality): Promise<Result<Blob>> {
    const response = await this.api.request<Blob>({
      url: `${this.apiUrlDomain}/${appId}/download-qr-code`,
      method: HttpMethod.POST,
      data: { functionality },
      responseType: 'blob'
    })

    if (!isSuccessResponse(response)) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(response.data)
  }

  async updateVpnSettings (appId: number, payload: VpnSetting): Promise<Result<undefined>> {
    const response = await this.put<undefined>(`${this.apiUrlDomain}/${appId}/vpn`, {
      terminal: payload.terminal,
      id: payload.id,
      ip: payload.ip,
      cnpj_client: payload.cnpj_client,
      cnpj_integrator: payload.cnpj_integrator,
      otp: payload.otp
    })

    if (response == null || !isSuccessResponse(response)) {
      return Result.ofError(
        new BaseGatewayErrorDTO().deserialize(response?.data as BaseGatewayErrorDTO) ?? <BaseError>{ code: ErrorCodes.Unknown }
      )
    }

    return Result.ofValue(undefined)
  }
}
